import axios, { AxiosError } from 'axios';
import { API_CONFIG } from '../config/api.config';
import {
  ColetaAceite,
  ColetaBusca,
  ColetaCredencial,
  ColetaCredencialNova,
  ColetaJob,
  ColetaOperacao,
  ColetaSaude,
  ColetaSessao,
  ColetaVigilancia,
  ColetaFluxo,
  ColetaFluxoPedido,
  ColetaHistoricoDetalhe,
  ColetaHistoricoLista,
} from '../types/coleta';

const http = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: 8000,
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function erroDeColeta(error: unknown, fallback = 'Não foi possível concluir a coleta.'): string {
  const resposta = axios.isAxiosError(error)
    ? (error as AxiosError<{ erro?: unknown; message?: unknown }>).response
    : undefined;
  if (resposta?.status === 429) {
    return 'A fila está cheia. Tente de novo em instantes.';
  }
  const corpo = resposta?.data;
  const texto = [corpo?.erro, corpo?.message]
    .find((valor): valor is string => typeof valor === 'string' && valor.trim().length > 0)
    ?.trim() ?? '';
  if (texto && !/(?:bearer|authorization|password|senha)\s*[=:]\s*\S+/i.test(texto)) {
    return texto;
  }
  return fallback;
}

export async function listarCredenciais(): Promise<ColetaCredencial[]> {
  const response = await http.get<ColetaCredencial[]>('/coleta/credenciais');
  return response.data;
}

export async function cadastrarCredencial(pedido: ColetaCredencialNova): Promise<ColetaCredencial> {
  const timeout = pedido.tipo === 'lemit' || pedido.tipo === 'vtme-gettel' || pedido.tipo === 'vtme-unity' ? 45000 : undefined;
  const response = await http.post<ColetaCredencial>('/coleta/credenciais', pedido, timeout ? { timeout } : undefined);
  return response.data;
}

export async function ativarP2b(id: string): Promise<ColetaSessao> {
  const response = await http.post<ColetaSessao>(`/coleta/credenciais/${id}/ativar`);
  return response.data;
}

export async function reiniciarP2b(id: string): Promise<ColetaSessao> {
  const response = await http.post<ColetaSessao>(`/coleta/credenciais/${id}/reiniciar`);
  return response.data;
}

export async function excluirCredencial(id: string): Promise<void> {
  await http.delete(`/coleta/credenciais/${id}`);
}

export async function suspenderContagemP2b(id: string, preenchido: boolean): Promise<ColetaSessao> {
  const response = await http.post<ColetaSessao>(`/coleta/credenciais/${id}/contagem`, { preenchido });
  return response.data;
}

export async function segurarContagemP2b(id: string, segurar: boolean): Promise<ColetaSessao> {
  const response = await http.post<ColetaSessao>(`/coleta/credenciais/${id}/segurar`, { segurar });
  return response.data;
}

export async function trocarUsuarioP2b(id: string, identificador: string, senha = '', timeoutMs?: number): Promise<ColetaSessao> {
  const response = await http.post<ColetaSessao>(
    `/coleta/credenciais/${id}/trocar`,
    { identificador, senha },
    timeoutMs ? { timeout: timeoutMs } : undefined,
  );
  return response.data;
}

export async function renovarP2b(id: string): Promise<ColetaSessao> {
  const response = await http.post<ColetaSessao>(`/coleta/credenciais/${id}/renovar`);
  return response.data;
}

export async function entrarP2b(id: string, token: string): Promise<ColetaSessao> {
  const response = await http.post<ColetaSessao>(`/coleta/credenciais/${id}/entrar`, { token });
  return response.data;
}

export async function desligarCredencial(id: string): Promise<void> {
  await http.post(`/coleta/credenciais/${id}/desligar`);
}

export async function obterConfiguracao(): Promise<ColetaVigilancia> {
  const response = await http.get<ColetaVigilancia>('/coleta/configuracao');
  return response.data;
}

export async function salvarConfiguracao(pedido: ColetaVigilancia): Promise<ColetaVigilancia> {
  const response = await http.post<ColetaVigilancia>('/coleta/configuracao', pedido);
  return response.data;
}

export async function obterSaude(): Promise<ColetaSaude> {
  const response = await http.get<ColetaSaude>('/coleta/saude');
  return response.data;
}

export async function obterOperacao(pagina = 1, tamanho = 5): Promise<ColetaOperacao> {
  const response = await http.get<ColetaOperacao>('/coleta/operacao', { params: { pagina, tamanho } });
  return response.data;
}

export async function obterHistorico(pagina = 1, tamanho = 5): Promise<ColetaHistoricoLista> {
  const response = await http.get<ColetaHistoricoLista>('/coleta/historico', { params: { pagina, tamanho } });
  return response.data;
}

export async function obterHistoricoCnpj(cnpj: string): Promise<ColetaHistoricoDetalhe> {
  const response = await http.get<ColetaHistoricoDetalhe>(`/coleta/historico/${encodeURIComponent(cnpj)}`);
  return response.data;
}

export async function buscarP2b(pedido: ColetaBusca): Promise<ColetaAceite> {
  const response = await http.post<ColetaAceite>('/coleta/p2b/buscas', pedido);
  return response.data;
}

export async function buscarLemit(pedido: ColetaBusca): Promise<ColetaAceite> {
  const response = await http.post<ColetaAceite>('/coleta/lemit/consultas', pedido);
  return response.data;
}

export async function pedirFluxo(pedido: ColetaFluxoPedido): Promise<ColetaFluxo> {
  const response = await http.post<ColetaFluxo>('/coleta/fluxos', pedido);
  return response.data;
}

export async function consultarFluxo(cnpj: string): Promise<ColetaFluxo> {
  const response = await http.get<ColetaFluxo>(`/coleta/fluxos/${encodeURIComponent(cnpj)}`);
  return response.data;
}

export async function priorizarFluxo(cnpj: string): Promise<ColetaFluxo> {
  const response = await http.post<ColetaFluxo>(`/coleta/fluxos/${encodeURIComponent(cnpj)}/priorizar`);
  return response.data;
}

export async function retentarFluxo(cnpj: string): Promise<ColetaFluxo> {
  const response = await http.post<ColetaFluxo>(`/coleta/fluxos/${encodeURIComponent(cnpj)}/retentar`);
  return response.data;
}

export async function obterJob(jobId: string): Promise<ColetaJob> {
  const response = await http.get<ColetaJob>(`/coleta/jobs/${jobId}`);
  return response.data;
}

export async function consultarVtme(chave: string): Promise<ColetaJob> {
  const response = await http.get<ColetaJob>(`/coleta/vtme/validacoes/${encodeURIComponent(chave)}`);
  return response.data;
}

export async function retentarVtme(chave: string): Promise<ColetaJob> {
  const response = await http.post<ColetaJob>(`/coleta/vtme/validacoes/${encodeURIComponent(chave)}/retentar`);
  return response.data;
}
